/* eslint-disable react/no-danger */
import addToCartKWC from '@/api/post/addToCartKWC';
import Popup from '@/kenwood/configurator/components/Popup';
import { useConfiguratorContext } from '@/kenwood/configurator/context';
import { callGtmAddToCart } from '@/utils/Controllers/GTM/Operations/Cart';
import {
  pushAdapterAlert,
  pushAdapterInfo,
  pushAdapterTwistSystem,
  pushOwnerAdapterAdd,
  pushProductSelectCta,
} from '@/utils/Controllers/GTM/Operations/Kenwood/Configurator';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

const CHEF_OWNER_CATEGORY_CODE = 'KW_CONFIG_CHEF_OWNER';

const ConnectionAddPopup = ({
  inStock,
  adaptorImage,
  onClose,
  onContinue,
  onAdd,
  t,
}) => (
  <div className='ken-configurator__generic-popup'>
    <button
      className='ken-configurator__generic-popup-close'
      type='button'
      onClick={onClose}
    >
      <img
        className='ken-configurator__generic-popup-close-img'
        src={`${DLG.config.distResourcePath}/assets/images/kenwood/configurator/close.svg`}
        alt={t('configurator:configurator.alt.close')}
      />
    </button>
    <div className='ken-configurator__generic-popup-content'>
      <div
        className='ken-configurator__generic-popup-title'
        dangerouslySetInnerHTML={{
          __html: t('configurator:configurator.connectionflow.add.title'),
        }}
      />
      <div
        className='ken-configurator__generic-popup-desc'
        dangerouslySetInnerHTML={{
          __html: t(
            `configurator:configurator.connectionflow.add.description${
              inStock ? '' : '.outofstock'
            }`,
          ),
        }}
      />
      <div className='ken-configurator__generic-popup-desc'>
        <div className='ken-configurator__generic-popup-desc-flexboxes'>
          {adaptorImage && <img src={adaptorImage} alt='adaptor' />}
        </div>
      </div>
      {!inStock && (
        <div className='ken-configurator__generic-popup-desc'>
          <span className='out-of-stock-tag'>
            {t('common:product.out.of.stock.label')}
          </span>
        </div>
      )}
      <div className='ken-configurator__generic-popup-ctas'>
        <button
          type='button'
          className='ken-hero__textblock-cta ken-hero__textblock-cta--primary'
          disabled={!inStock}
          onClick={onAdd}
        >
          {t(
            `configurator:configurator.connectionflow.add.button.confirm${
              inStock ? '' : '.disabled'
            }`,
          )}
        </button>
        <button
          type='button'
          className='ken-hero__textblock-cta ken-hero__textblock-cta--secondary'
          onClick={onContinue}
        >
          {t(
            `configurator:configurator.connectionflow.add.button.cancel${
              inStock ? '' : '.alternative'
            }`,
          )}
        </button>
      </div>
    </div>
  </div>
);

const ConnectionLegendPopup = ({ connectionsData, onClose, t }) => (
  <div className='ken-configurator__generic-popup'>
    <button
      className='ken-configurator__generic-popup-close'
      type='button'
      onClick={onClose}
    >
      <img
        className='ken-configurator__generic-popup-close-img'
        src={`${DLG.config.distResourcePath}/assets/images/kenwood/configurator/close.svg`}
        alt={t('configurator:configurator.alt.close')}
      />
    </button>
    <div className='ken-configurator__generic-popup-content'>
      <div
        className='ken-configurator__generic-popup-title'
        dangerouslySetInnerHTML={{
          __html: t('configurator:configurator.connectionflow.legend.title'),
        }}
      />
      <div className='ken-configurator__generic-popup-desc'>
        <div className='ken-configurator__generic-popup-desc-attachments'>
          {connectionsData &&
            connectionsData.connections.map(c => (
              <div key={`connection-${c.id}`}>
                <img src={c.image?.url} alt={c.image?.altText} />
                <h3>{c.title}</h3>
                <p>{c.description}</p>
              </div>
            ))}
        </div>
      </div>
    </div>
  </div>
);

const ConnectionAskPopup = ({ onClose, onContinue, onInfo, onOpen, t }) => (
  <div className='ken-configurator__generic-popup'>
    <button
      className='ken-configurator__generic-popup-close'
      type='button'
      onClick={onClose}
    >
      <img
        className='ken-configurator__generic-popup-close-img'
        src={`${DLG.config.distResourcePath}/assets/images/kenwood/configurator/close.svg`}
        alt={t('configurator:configurator.alt.close')}
      />
    </button>
    <div className='ken-configurator__generic-popup-content'>
      <div
        className='ken-configurator__generic-popup-title'
        dangerouslySetInnerHTML={{
          __html: t('configurator:configurator.connectionflow.ask.title'),
        }}
      />
      <div
        className='ken-configurator__generic-popup-desc'
        dangerouslySetInnerHTML={{
          __html: t('configurator:configurator.connectionflow.ask.description'),
        }}
      />
      <div className='ken-configurator__generic-popup-desc'>
        <div className='ken-configurator__generic-popup-desc-flexboxes'>
          <img
            src={`${DLG.config.distResourcePath}/assets/images/kenwood/configurator/connection.svg`}
            alt='connection'
          />
        </div>
      </div>
      <div className='ken-configurator__generic-popup-desc'>
        <button type='button' className='ken-button--link' onClick={onInfo}>
          {t('configurator:configurator.connectionflow.ask.action.readmore')}
        </button>
      </div>
      <div className='ken-configurator__generic-popup-ctas'>
        <button
          type='button'
          className='ken-hero__textblock-cta ken-hero__textblock-cta--primary'
          onClick={onContinue}
        >
          {t('configurator:configurator.connectionflow.ask.button.confirm')}
        </button>
        <button
          type='button'
          className='ken-hero__textblock-cta ken-hero__textblock-cta--secondary'
          onClick={onOpen}
        >
          {t('configurator:configurator.connectionflow.ask.button.cancel')}
        </button>
      </div>
    </div>
  </div>
);

const ConnectionFlow = ({ slideData }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [
    { connectionsData, isOwner, productsData },
    { setSelectedConnection, setSelectedProduct, setSelectedCategory },
  ] = useConfiguratorContext();
  const [isConnectionAskPopupOpen, setIsConnectionAskPopupOpen] = useState(
    false,
  );
  const [
    isConnectionLegendPopupOpen,
    setIsConnectionLegendPopupOpen,
  ] = useState(false);
  const [isConnectionAddPopupOpen, setIsConnectionAddPopupOpen] = useState(
    false,
  );
  const adaptorImage = _.has(productsData, '[0].adaptor.images[0].url')
    ? productsData[0].adaptor.images[0].url.replace(
        /com\/.*\/assets/,
        'com/230x166/assets',
      )
    : null;
  const selectSlide = () => {
    pushProductSelectCta(isOwner, slideData?.name);
    if (isOwner) {
      setSelectedCategory(slideData.element);

      if (slideData.code === CHEF_OWNER_CATEGORY_CODE) {
        setIsConnectionAskPopupOpen(true);
      } else {
        history.push(`${DLG.config.encodedContextPath}/configurator/step2`);
      }
    } else {
      addToCartKWC({
        skuCode: slideData.activeVariant.code,
        qty: 1,
        successCB: data => {
          callGtmAddToCart(data.cartModification.entry);
          setSelectedProduct(slideData.activeVariant);

          // Find the category to which the product belongs
          productsData.forEach(c => {
            if (c.products.find(p => p.code === slideData.activeVariant.code)) {
              setSelectedCategory(c);
            }
          });
          history.push(`${DLG.config.encodedContextPath}/configurator/step2`);
        },
      });
    }
  };
  const selectConnection = () => {
    addToCartKWC({
      skuCode: slideData.element.adaptor.code,
      qty: 1,
      successCB: data => {
        callGtmAddToCart(data.cartModification.entry);
        pushOwnerAdapterAdd();
        setSelectedConnection(slideData.element.adaptor);
        setIsConnectionAddPopupOpen(false);
        history.push(`${DLG.config.encodedContextPath}/configurator/step2`);
      },
    });
  };

  useEffect(() => {
    if (isConnectionAskPopupOpen) {
      pushAdapterTwistSystem();
    }
    if (isConnectionAddPopupOpen) {
      pushAdapterAlert();
    }
    if (isConnectionLegendPopupOpen) {
      pushAdapterInfo();
    }
  }, [
    isConnectionAskPopupOpen,
    isConnectionAddPopupOpen,
    isConnectionLegendPopupOpen,
  ]);

  return (
    <>
      <div className='ken-configurator__owner-banner-cta-mobile'>
        <button
          type='button'
          className='ken-button--primary'
          onClick={selectSlide}
        >
          {t('configurator:configurator.productscarousel.slide.action.select')}
        </button>
      </div>
      <Popup isOpen={isConnectionAskPopupOpen}>
        <ConnectionAskPopup
          onClose={() => {
            setIsConnectionAskPopupOpen(false);
          }}
          onInfo={() => {
            setIsConnectionAskPopupOpen(false);
            setIsConnectionLegendPopupOpen(true);
          }}
          onContinue={() => {
            setIsConnectionAskPopupOpen(false);
            history.push(`${DLG.config.encodedContextPath}/configurator/step2`);
          }}
          onOpen={() => {
            setIsConnectionAskPopupOpen(false);
            setIsConnectionAddPopupOpen(true);
          }}
          t={t}
        />
      </Popup>
      <Popup isOpen={isConnectionLegendPopupOpen}>
        <ConnectionLegendPopup
          connectionsData={connectionsData}
          onClose={() => {
            setIsConnectionAskPopupOpen(true);
            setIsConnectionLegendPopupOpen(false);
          }}
          t={t}
        />
      </Popup>
      <Popup isOpen={isConnectionAddPopupOpen}>
        <ConnectionAddPopup
          inStock={
            slideData.element?.adaptor &&
            slideData.element.adaptor.stock.stockLevelStatus !== 'outOfStock'
          }
          adaptorImage={adaptorImage}
          onClose={() => {
            setIsConnectionAddPopupOpen(false);
          }}
          onContinue={() => {
            setIsConnectionAddPopupOpen(false);
            history.push(`${DLG.config.encodedContextPath}/configurator/step2`);
          }}
          onAdd={selectConnection}
          t={t}
        />
      </Popup>
    </>
  );
};

ConnectionAddPopup.propTypes = {
  inStock: PropTypes.bool,
  adaptorImage: PropTypes.string,
  onClose: PropTypes.func,
  onContinue: PropTypes.func,
  onAdd: PropTypes.func,
  t: PropTypes.func,
};

ConnectionAddPopup.defaultProps = {
  inStock: false,
  adaptorImage: null,
  onClose: () => {},
  onContinue: () => {},
  onAdd: () => {},
  t: () => {},
};

ConnectionLegendPopup.propTypes = {
  connectionsData: PropTypes.objectOf(PropTypes.any),
  onClose: PropTypes.func,
  t: PropTypes.func,
};

ConnectionLegendPopup.defaultProps = {
  connectionsData: null,
  onClose: () => {},
  t: () => {},
};

ConnectionAskPopup.propTypes = {
  onClose: PropTypes.func,
  onContinue: PropTypes.func,
  onInfo: PropTypes.func,
  onOpen: PropTypes.func,
  t: PropTypes.func,
};

ConnectionAskPopup.defaultProps = {
  onClose: () => {},
  onContinue: () => {},
  onInfo: () => {},
  onOpen: () => {},
  t: () => {},
};

ConnectionFlow.propTypes = {
  slideData: PropTypes.objectOf(PropTypes.any),
};

ConnectionFlow.defaultProps = {
  slideData: null,
};

export default ConnectionFlow;
